export const LEAVE_TYPE_REGISTRY_SCHEMA_VERSION = '1.3.0' as const;

export type LeaveTypeOperationalKey =
  | 'ANNUAL'
  | 'PERSONAL_GENERAL_STAFF'
  | 'PERSONAL_NURSES'
  | 'CARERS_EXTRA_GENERAL_STAFF'
  | 'LONG_SERVICE'
  | 'COMPASSIONATE'
  | 'UNPAID'
  | 'PARENTAL'
  | 'COMMUNITY_SERVICE'
  | 'FAMILY_DOMESTIC_VIOLENCE'
  | 'PUBLIC_HOLIDAY'
  | 'WORKERS_COMP_NOT_WORKED'
  | 'ANNUAL_ADDITIONAL_SHIFTWORKER_WEEK'
  | 'TOIL'
  | 'ADO'
  | 'CASH_OUT'
  | 'TERMINATION_PAYOUT';

export const LEAVE_TYPE_SUPPORTED_XERO_MAPPING_KEYS = Object.freeze([
  'ANNUAL',
  'PERSONAL_GENERAL_STAFF',
  'PERSONAL_NURSES',
  'CARERS_EXTRA_GENERAL_STAFF',
  'LONG_SERVICE',
  'COMPASSIONATE',
  'UNPAID',
] as const);

export type LeaveTypeSupportedXeroMappingKey =
  (typeof LEAVE_TYPE_SUPPORTED_XERO_MAPPING_KEYS)[number];

export type LeaveTypeXeroMappingKey = LeaveTypeSupportedXeroMappingKey | null;

export type LeaveTypeHeldReasonCode =
  | 'HELD_LSL_AUTHORITY_AND_PORTABILITY_UNPROVEN'
  | 'HELD_COMPASSIONATE_PAYROLL_AND_SYNC_UNPROVEN'
  | 'HELD_PARENTAL_PROVIDER_CONFIGURATION_REQUIRED'
  | 'HELD_COMMUNITY_SERVICE_PROVIDER_CONFIGURATION_REQUIRED'
  | 'HELD_FDV_PRIVACY_AND_PAYROLL_CONFIGURATION_REQUIRED'
  | 'HELD_ANNUAL_ADDITIONAL_WEEK_CONFIGURATION_REQUIRED'
  | 'HELD_TOIL_EXACT_ACCRUAL_SOURCE_REQUIRED'
  | 'HELD_ADO_EXACT_ACCRUAL_SOURCE_REQUIRED';

export type LeaveTypeRequestability =
  | 'ALWAYS'
  | 'EVIDENCE_REQUIRED'
  | 'PROVIDER_CONFIGURED'
  | 'HELD'
  | 'NEVER';

export type LeaveTypeCohort =
  | 'ALL_STAFF'
  | 'GENERAL_STAFF'
  | 'NURSES'
  | 'SHIFTWORKER'
  | 'PROVIDER_CONFIGURED';

export type LeaveTypeEmploymentEligibility =
  | 'TENANT_EFFECTIVE_EMPLOYMENT_AND_POLICY_AUTHORITY_REQUIRED'
  | 'NOT_APPLICABLE';

export type LeaveTypeUnit = 'HOURS' | 'NOT_APPLICABLE';

export type LeaveTypeBalanceSource = 'ACCRUED' | 'NOT_BALANCE';

export type LeaveTypeNegativeBalanceApproval = 'NOT_APPLICABLE' | 'PROVIDER_CONFIGURED' | 'HELD';

export type LeaveTypeEvidence = 'NONE' | 'EVIDENCE_REQUIRED' | 'EXTERNAL_AUTHORITY_REQUIRED';

export type LeaveTypeConfidentiality = 'STANDARD' | 'CONFIDENTIAL' | 'RESTRICTED';

export type LeaveTypeApproval =
  | 'STANDARD'
  | 'EVIDENCE_REQUIRED'
  | 'PROVIDER_CONFIGURED'
  | 'SEPARATE_CASE'
  | 'SEPARATE_EVENT';

export type LeaveTypeIndependentChecker = 'NONE' | 'PROVIDER_CONFIGURED' | 'REQUIRED';

export type LeaveTypeCoverage = 'ABSENCE_COVERAGE' | 'SEPARATE_CASE' | 'SEPARATE_EVENT';

export type LeaveTypePayroll = 'PROVIDER_CONFIGURED' | 'HELD' | 'SEPARATE_CASE' | 'SEPARATE_EVENT';

export type LeaveTypeLifecycleSetting =
  | 'NOT_APPLICABLE'
  | 'PROVIDER_CONFIGURED'
  | 'HELD'
  | 'EXTERNAL_AUTHORITY_REQUIRED';

export type LeaveTypeJurisdictionAuthority = 'NOT_APPLICABLE' | 'EXTERNAL_AUTHORITY_REQUIRED';

export type LeaveTypeEntitlementSourceAuthority = 'NOT_APPLICABLE' | 'EXTERNAL_AUTHORITY_REQUIRED';

export type LeaveTypePortableSchemeSourceAuthority =
  | 'NOT_APPLICABLE'
  | 'EXTERNAL_AUTHORITY_REQUIRED';

export type LeaveTypeClauseVerificationArtifactRequirement = 'NOT_APPLICABLE' | 'REQUIRED';

export type LeaveTypeClauseVerificationArtifactIdentityRequirement = 'NOT_APPLICABLE' | 'REQUIRED';

export type LeaveTypeClauseVerificationArtifactVersionRequirement = 'NOT_APPLICABLE' | 'REQUIRED';

export type LeaveTypePushPull = 'PROVIDER_CONFIGURED' | 'HELD' | 'NOT_APPLICABLE';

export type LeaveTypeProviderConfigurableField =
  | 'requestability'
  | 'cohort'
  | 'negativeBalanceApproval'
  | 'approval'
  | 'independentChecker'
  | 'payroll'
  | 'push'
  | 'pull'
  | 'lifecycle.annualLoading'
  | 'lifecycle.cashOut'
  | 'lifecycle.finalPay'
  | 'lifecycle.publicHolidayIntersection'
  | 'lifecycle.portableAuthority'
  | 'lifecycle.forecastAccrualSource';

export type LeaveTypeRegistryRow = {
  readonly operationalKey: LeaveTypeOperationalKey;
  readonly sourceIdentity: string;
  readonly label: string;
  readonly display: {
    readonly family: string;
    readonly summaryOnly: true;
    readonly certifyingKey: false;
  };
  readonly aliases: readonly string[];
  readonly requestability: LeaveTypeRequestability;
  readonly cohort: LeaveTypeCohort;
  readonly employmentEligibility: LeaveTypeEmploymentEligibility;
  readonly unit: LeaveTypeUnit;
  readonly balanceSource: LeaveTypeBalanceSource;
  readonly drawsDownBalance: boolean;
  readonly negativeBalanceApproval: LeaveTypeNegativeBalanceApproval;
  readonly evidence: LeaveTypeEvidence;
  readonly confidentiality: LeaveTypeConfidentiality;
  readonly approval: LeaveTypeApproval;
  readonly independentChecker: LeaveTypeIndependentChecker;
  readonly coverage: LeaveTypeCoverage;
  readonly payroll: LeaveTypePayroll;
  readonly xero: {
    readonly mappingKey: LeaveTypeXeroMappingKey;
    readonly requiresConfirmedTenantLeaveTypeId: boolean;
    readonly requiresEmployeePayTemplate: boolean;
    readonly requiresExactTenantMappingBeforeEnable: boolean;
  };
  readonly lifecycle: {
    readonly annualLoading: LeaveTypeLifecycleSetting;
    readonly cashOut: LeaveTypeLifecycleSetting;
    readonly finalPay: LeaveTypeLifecycleSetting;
    readonly publicHolidayIntersection: LeaveTypeLifecycleSetting;
    readonly portableAuthority: LeaveTypeLifecycleSetting;
    readonly forecastAccrualSource: LeaveTypeLifecycleSetting;
  };
  readonly authority: {
    readonly jurisdiction: LeaveTypeJurisdictionAuthority;
    readonly entitlementSource: LeaveTypeEntitlementSourceAuthority;
    readonly portableSchemeSource: LeaveTypePortableSchemeSourceAuthority;
    readonly clauseVerificationArtifact: LeaveTypeClauseVerificationArtifactRequirement;
    readonly clauseVerificationArtifactIdentity: LeaveTypeClauseVerificationArtifactIdentityRequirement;
    readonly clauseVerificationArtifactVersion: LeaveTypeClauseVerificationArtifactVersionRequirement;
  };
  readonly providerConfigurableFlags: readonly LeaveTypeProviderConfigurableField[];
  readonly requiresExactAccrualSourceLink: boolean;
  readonly push: LeaveTypePushPull;
  readonly pull: LeaveTypePushPull;
  readonly heldReason: LeaveTypeHeldReasonCode | null;
};

export const LEAVE_TYPE_REGISTRY = Object.freeze({
  ANNUAL: Object.freeze({
    operationalKey: 'ANNUAL',
    sourceIdentity: 'STATURA_ANNUAL',
    label: 'Annual leave',
    display: Object.freeze({
      family: 'annual',
      summaryOnly: true,
      certifyingKey: false,
    }),
    aliases: Object.freeze(['annual leave', 'recreation leave', 'al']),
    requestability: 'ALWAYS',
    cohort: 'ALL_STAFF',
    employmentEligibility: 'TENANT_EFFECTIVE_EMPLOYMENT_AND_POLICY_AUTHORITY_REQUIRED',
    unit: 'HOURS',
    balanceSource: 'ACCRUED',
    drawsDownBalance: true,
    negativeBalanceApproval: 'PROVIDER_CONFIGURED',
    evidence: 'NONE',
    confidentiality: 'STANDARD',
    approval: 'STANDARD',
    independentChecker: 'NONE',
    coverage: 'ABSENCE_COVERAGE',
    payroll: 'PROVIDER_CONFIGURED',
    xero: Object.freeze({
      mappingKey: 'ANNUAL',
      requiresConfirmedTenantLeaveTypeId: true,
      requiresEmployeePayTemplate: true,
      requiresExactTenantMappingBeforeEnable: true,
    }),
    lifecycle: Object.freeze({
      annualLoading: 'PROVIDER_CONFIGURED',
      cashOut: 'PROVIDER_CONFIGURED',
      finalPay: 'PROVIDER_CONFIGURED',
      publicHolidayIntersection: 'PROVIDER_CONFIGURED',
      portableAuthority: 'PROVIDER_CONFIGURED',
      forecastAccrualSource: 'PROVIDER_CONFIGURED',
    }),
    authority: Object.freeze({
      jurisdiction: 'NOT_APPLICABLE',
      entitlementSource: 'NOT_APPLICABLE',
      portableSchemeSource: 'NOT_APPLICABLE',
      clauseVerificationArtifact: 'NOT_APPLICABLE',
      clauseVerificationArtifactIdentity: 'NOT_APPLICABLE',
      clauseVerificationArtifactVersion: 'NOT_APPLICABLE',
    }),
    providerConfigurableFlags: Object.freeze([
      'negativeBalanceApproval',
      'lifecycle.annualLoading',
      'lifecycle.cashOut',
      'lifecycle.finalPay',
      'lifecycle.publicHolidayIntersection',
      'lifecycle.portableAuthority',
      'lifecycle.forecastAccrualSource',
      'payroll',
      'push',
      'pull',
    ] as const satisfies readonly LeaveTypeProviderConfigurableField[]),
    requiresExactAccrualSourceLink: false,
    push: 'PROVIDER_CONFIGURED',
    pull: 'PROVIDER_CONFIGURED',
    heldReason: null,
  }),
  PERSONAL_GENERAL_STAFF: Object.freeze({
    operationalKey: 'PERSONAL_GENERAL_STAFF',
    sourceIdentity: 'STATURA_PERSONAL_GENERAL_STAFF',
    label: 'Personal / carer’s leave (general staff)',
    display: Object.freeze({
      family: 'personal',
      summaryOnly: true,
      certifyingKey: false,
    }),
    aliases: Object.freeze([
      'personal leave (general staff)',
      'sick leave (general staff)',
      'carer’s leave (general staff)',
    ]),
    requestability: 'EVIDENCE_REQUIRED',
    cohort: 'GENERAL_STAFF',
    employmentEligibility: 'TENANT_EFFECTIVE_EMPLOYMENT_AND_POLICY_AUTHORITY_REQUIRED',
    unit: 'HOURS',
    balanceSource: 'ACCRUED',
    drawsDownBalance: true,
    negativeBalanceApproval: 'PROVIDER_CONFIGURED',
    evidence: 'EVIDENCE_REQUIRED',
    confidentiality: 'CONFIDENTIAL',
    approval: 'EVIDENCE_REQUIRED',
    independentChecker: 'NONE',
    coverage: 'ABSENCE_COVERAGE',
    payroll: 'PROVIDER_CONFIGURED',
    xero: Object.freeze({
      mappingKey: 'PERSONAL_GENERAL_STAFF',
      requiresConfirmedTenantLeaveTypeId: true,
      requiresEmployeePayTemplate: true,
      requiresExactTenantMappingBeforeEnable: true,
    }),
    lifecycle: Object.freeze({
      annualLoading: 'NOT_APPLICABLE',
      cashOut: 'NOT_APPLICABLE',
      finalPay: 'HELD',
      publicHolidayIntersection: 'PROVIDER_CONFIGURED',
      portableAuthority: 'NOT_APPLICABLE',
      forecastAccrualSource: 'PROVIDER_CONFIGURED',
    }),
    authority: Object.freeze({
      jurisdiction: 'NOT_APPLICABLE',
      entitlementSource: 'NOT_APPLICABLE',
      portableSchemeSource: 'NOT_APPLICABLE',
      clauseVerificationArtifact: 'NOT_APPLICABLE',
      clauseVerificationArtifactIdentity: 'NOT_APPLICABLE',
      clauseVerificationArtifactVersion: 'NOT_APPLICABLE',
    }),
    providerConfigurableFlags: Object.freeze([
      'negativeBalanceApproval',
      'lifecycle.publicHolidayIntersection',
      'lifecycle.forecastAccrualSource',
      'payroll',
      'push',
      'pull',
    ] as const satisfies readonly LeaveTypeProviderConfigurableField[]),
    requiresExactAccrualSourceLink: false,
    push: 'PROVIDER_CONFIGURED',
    pull: 'PROVIDER_CONFIGURED',
    heldReason: null,
  }),
  PERSONAL_NURSES: Object.freeze({
    operationalKey: 'PERSONAL_NURSES',
    sourceIdentity: 'STATURA_PERSONAL_NURSES',
    label: 'Personal / carer’s leave (nurses)',
    display: Object.freeze({
      family: 'personal',
      summaryOnly: true,
      certifyingKey: false,
    }),
    aliases: Object.freeze([
      'personal leave (nurses)',
      'sick leave (nurses)',
      'carer’s leave (nurses)',
    ]),
    requestability: 'EVIDENCE_REQUIRED',
    cohort: 'NURSES',
    employmentEligibility: 'TENANT_EFFECTIVE_EMPLOYMENT_AND_POLICY_AUTHORITY_REQUIRED',
    unit: 'HOURS',
    balanceSource: 'ACCRUED',
    drawsDownBalance: true,
    negativeBalanceApproval: 'PROVIDER_CONFIGURED',
    evidence: 'EVIDENCE_REQUIRED',
    confidentiality: 'CONFIDENTIAL',
    approval: 'EVIDENCE_REQUIRED',
    independentChecker: 'NONE',
    coverage: 'ABSENCE_COVERAGE',
    payroll: 'PROVIDER_CONFIGURED',
    xero: Object.freeze({
      mappingKey: 'PERSONAL_NURSES',
      requiresConfirmedTenantLeaveTypeId: true,
      requiresEmployeePayTemplate: true,
      requiresExactTenantMappingBeforeEnable: true,
    }),
    lifecycle: Object.freeze({
      annualLoading: 'NOT_APPLICABLE',
      cashOut: 'NOT_APPLICABLE',
      finalPay: 'HELD',
      publicHolidayIntersection: 'PROVIDER_CONFIGURED',
      portableAuthority: 'NOT_APPLICABLE',
      forecastAccrualSource: 'PROVIDER_CONFIGURED',
    }),
    authority: Object.freeze({
      jurisdiction: 'NOT_APPLICABLE',
      entitlementSource: 'NOT_APPLICABLE',
      portableSchemeSource: 'NOT_APPLICABLE',
      clauseVerificationArtifact: 'NOT_APPLICABLE',
      clauseVerificationArtifactIdentity: 'NOT_APPLICABLE',
      clauseVerificationArtifactVersion: 'NOT_APPLICABLE',
    }),
    providerConfigurableFlags: Object.freeze([
      'negativeBalanceApproval',
      'lifecycle.publicHolidayIntersection',
      'lifecycle.forecastAccrualSource',
      'payroll',
      'push',
      'pull',
    ] as const satisfies readonly LeaveTypeProviderConfigurableField[]),
    requiresExactAccrualSourceLink: false,
    push: 'PROVIDER_CONFIGURED',
    pull: 'PROVIDER_CONFIGURED',
    heldReason: null,
  }),
  CARERS_EXTRA_GENERAL_STAFF: Object.freeze({
    operationalKey: 'CARERS_EXTRA_GENERAL_STAFF',
    sourceIdentity: 'STATURA_CARERS_EXTRA_GENERAL_STAFF',
    label: 'Extra carer’s leave (general staff)',
    display: Object.freeze({
      family: 'personal',
      summaryOnly: true,
      certifyingKey: false,
    }),
    aliases: Object.freeze([
      'extra carer’s leave (general staff)',
      'additional carer’s leave (general staff)',
    ]),
    requestability: 'EVIDENCE_REQUIRED',
    cohort: 'GENERAL_STAFF',
    employmentEligibility: 'TENANT_EFFECTIVE_EMPLOYMENT_AND_POLICY_AUTHORITY_REQUIRED',
    unit: 'HOURS',
    balanceSource: 'ACCRUED',
    drawsDownBalance: true,
    negativeBalanceApproval: 'PROVIDER_CONFIGURED',
    evidence: 'EVIDENCE_REQUIRED',
    confidentiality: 'CONFIDENTIAL',
    approval: 'EVIDENCE_REQUIRED',
    independentChecker: 'NONE',
    coverage: 'ABSENCE_COVERAGE',
    payroll: 'PROVIDER_CONFIGURED',
    xero: Object.freeze({
      mappingKey: 'CARERS_EXTRA_GENERAL_STAFF',
      requiresConfirmedTenantLeaveTypeId: true,
      requiresEmployeePayTemplate: true,
      requiresExactTenantMappingBeforeEnable: true,
    }),
    lifecycle: Object.freeze({
      annualLoading: 'NOT_APPLICABLE',
      cashOut: 'NOT_APPLICABLE',
      finalPay: 'HELD',
      publicHolidayIntersection: 'PROVIDER_CONFIGURED',
      portableAuthority: 'NOT_APPLICABLE',
      forecastAccrualSource: 'PROVIDER_CONFIGURED',
    }),
    authority: Object.freeze({
      jurisdiction: 'NOT_APPLICABLE',
      entitlementSource: 'NOT_APPLICABLE',
      portableSchemeSource: 'NOT_APPLICABLE',
      clauseVerificationArtifact: 'NOT_APPLICABLE',
      clauseVerificationArtifactIdentity: 'NOT_APPLICABLE',
      clauseVerificationArtifactVersion: 'NOT_APPLICABLE',
    }),
    providerConfigurableFlags: Object.freeze([
      'negativeBalanceApproval',
      'lifecycle.publicHolidayIntersection',
      'lifecycle.forecastAccrualSource',
      'payroll',
      'push',
      'pull',
    ] as const satisfies readonly LeaveTypeProviderConfigurableField[]),
    requiresExactAccrualSourceLink: false,
    push: 'PROVIDER_CONFIGURED',
    pull: 'PROVIDER_CONFIGURED',
    heldReason: null,
  }),
  LONG_SERVICE: Object.freeze({
    operationalKey: 'LONG_SERVICE',
    sourceIdentity: 'STATURA_LONG_SERVICE',
    label: 'Long service leave',
    display: Object.freeze({
      family: 'long_service',
      summaryOnly: true,
      certifyingKey: false,
    }),
    aliases: Object.freeze(['long service leave', 'lsl']),
    requestability: 'ALWAYS',
    cohort: 'ALL_STAFF',
    employmentEligibility: 'TENANT_EFFECTIVE_EMPLOYMENT_AND_POLICY_AUTHORITY_REQUIRED',
    unit: 'HOURS',
    balanceSource: 'ACCRUED',
    drawsDownBalance: true,
    negativeBalanceApproval: 'PROVIDER_CONFIGURED',
    evidence: 'EVIDENCE_REQUIRED',
    confidentiality: 'STANDARD',
    approval: 'PROVIDER_CONFIGURED',
    independentChecker: 'PROVIDER_CONFIGURED',
    coverage: 'ABSENCE_COVERAGE',
    payroll: 'PROVIDER_CONFIGURED',
    xero: Object.freeze({
      mappingKey: 'LONG_SERVICE',
      requiresConfirmedTenantLeaveTypeId: true,
      requiresEmployeePayTemplate: true,
      requiresExactTenantMappingBeforeEnable: true,
    }),
    lifecycle: Object.freeze({
      annualLoading: 'NOT_APPLICABLE',
      cashOut: 'HELD',
      finalPay: 'HELD',
      publicHolidayIntersection: 'PROVIDER_CONFIGURED',
      portableAuthority: 'EXTERNAL_AUTHORITY_REQUIRED',
      forecastAccrualSource: 'HELD',
    }),
    authority: Object.freeze({
      jurisdiction: 'EXTERNAL_AUTHORITY_REQUIRED',
      entitlementSource: 'EXTERNAL_AUTHORITY_REQUIRED',
      portableSchemeSource: 'EXTERNAL_AUTHORITY_REQUIRED',
      clauseVerificationArtifact: 'REQUIRED',
      clauseVerificationArtifactIdentity: 'REQUIRED',
      clauseVerificationArtifactVersion: 'REQUIRED',
    }),
    providerConfigurableFlags: Object.freeze([
      'approval',
      'independentChecker',
      'negativeBalanceApproval',
      'lifecycle.publicHolidayIntersection',
      'payroll',
    ] as const satisfies readonly LeaveTypeProviderConfigurableField[]),
    requiresExactAccrualSourceLink: false,
    push: 'HELD',
    pull: 'HELD',
    // Registry 1.2.0 enables Medea request capture against its TAS policy.
    // Final-pay/forecast/portability authority and both Xero sync directions
    // remain independently held.
    heldReason: null,
  }),
  COMPASSIONATE: Object.freeze({
    operationalKey: 'COMPASSIONATE',
    sourceIdentity: 'STATURA_COMPASSIONATE',
    label: 'Compassionate leave',
    display: Object.freeze({
      family: 'compassionate',
      summaryOnly: true,
      certifyingKey: false,
    }),
    aliases: Object.freeze(['compassionate leave', 'bereavement leave']),
    requestability: 'EVIDENCE_REQUIRED',
    cohort: 'ALL_STAFF',
    employmentEligibility: 'TENANT_EFFECTIVE_EMPLOYMENT_AND_POLICY_AUTHORITY_REQUIRED',
    unit: 'HOURS',
    balanceSource: 'NOT_BALANCE',
    drawsDownBalance: false,
    negativeBalanceApproval: 'NOT_APPLICABLE',
    evidence: 'EVIDENCE_REQUIRED',
    confidentiality: 'CONFIDENTIAL',
    approval: 'EVIDENCE_REQUIRED',
    independentChecker: 'NONE',
    coverage: 'ABSENCE_COVERAGE',
    payroll: 'HELD',
    xero: Object.freeze({
      mappingKey: 'COMPASSIONATE',
      requiresConfirmedTenantLeaveTypeId: true,
      requiresEmployeePayTemplate: true,
      requiresExactTenantMappingBeforeEnable: true,
    }),
    lifecycle: Object.freeze({
      annualLoading: 'NOT_APPLICABLE',
      cashOut: 'NOT_APPLICABLE',
      finalPay: 'NOT_APPLICABLE',
      publicHolidayIntersection: 'PROVIDER_CONFIGURED',
      portableAuthority: 'NOT_APPLICABLE',
      forecastAccrualSource: 'NOT_APPLICABLE',
    }),
    authority: Object.freeze({
      jurisdiction: 'NOT_APPLICABLE',
      entitlementSource: 'NOT_APPLICABLE',
      portableSchemeSource: 'NOT_APPLICABLE',
      clauseVerificationArtifact: 'NOT_APPLICABLE',
      clauseVerificationArtifactIdentity: 'NOT_APPLICABLE',
      clauseVerificationArtifactVersion: 'NOT_APPLICABLE',
    }),
    providerConfigurableFlags: Object.freeze([
      'lifecycle.publicHolidayIntersection',
    ] as const satisfies readonly LeaveTypeProviderConfigurableField[]),
    requiresExactAccrualSourceLink: false,
    push: 'HELD',
    pull: 'HELD',
    heldReason: 'HELD_COMPASSIONATE_PAYROLL_AND_SYNC_UNPROVEN',
  }),
  UNPAID: Object.freeze({
    operationalKey: 'UNPAID',
    sourceIdentity: 'STATURA_UNPAID',
    label: 'Unpaid leave',
    display: Object.freeze({
      family: 'unpaid',
      summaryOnly: true,
      certifyingKey: false,
    }),
    aliases: Object.freeze(['unpaid leave', 'leave without pay', 'lwop']),
    requestability: 'PROVIDER_CONFIGURED',
    cohort: 'ALL_STAFF',
    employmentEligibility: 'TENANT_EFFECTIVE_EMPLOYMENT_AND_POLICY_AUTHORITY_REQUIRED',
    unit: 'HOURS',
    balanceSource: 'NOT_BALANCE',
    drawsDownBalance: false,
    negativeBalanceApproval: 'NOT_APPLICABLE',
    evidence: 'NONE',
    confidentiality: 'STANDARD',
    approval: 'PROVIDER_CONFIGURED',
    independentChecker: 'NONE',
    coverage: 'ABSENCE_COVERAGE',
    payroll: 'PROVIDER_CONFIGURED',
    xero: Object.freeze({
      mappingKey: 'UNPAID',
      requiresConfirmedTenantLeaveTypeId: true,
      requiresEmployeePayTemplate: true,
      requiresExactTenantMappingBeforeEnable: true,
    }),
    lifecycle: Object.freeze({
      annualLoading: 'NOT_APPLICABLE',
      cashOut: 'NOT_APPLICABLE',
      finalPay: 'NOT_APPLICABLE',
      publicHolidayIntersection: 'PROVIDER_CONFIGURED',
      portableAuthority: 'NOT_APPLICABLE',
      forecastAccrualSource: 'NOT_APPLICABLE',
    }),
    authority: Object.freeze({
      jurisdiction: 'NOT_APPLICABLE',
      entitlementSource: 'NOT_APPLICABLE',
      portableSchemeSource: 'NOT_APPLICABLE',
      clauseVerificationArtifact: 'NOT_APPLICABLE',
      clauseVerificationArtifactIdentity: 'NOT_APPLICABLE',
      clauseVerificationArtifactVersion: 'NOT_APPLICABLE',
    }),
    providerConfigurableFlags: Object.freeze([
      'requestability',
      'approval',
      'lifecycle.publicHolidayIntersection',
      'payroll',
      'push',
      'pull',
    ] as const satisfies readonly LeaveTypeProviderConfigurableField[]),
    requiresExactAccrualSourceLink: false,
    push: 'PROVIDER_CONFIGURED',
    pull: 'PROVIDER_CONFIGURED',
    heldReason: null,
  }),
  PARENTAL: Object.freeze({
    operationalKey: 'PARENTAL',
    sourceIdentity: 'STATURA_PARENTAL',
    label: 'Parental leave',
    display: Object.freeze({
      family: 'parental',
      summaryOnly: true,
      certifyingKey: false,
    }),
    aliases: Object.freeze([
      'parental leave',
      'maternity leave',
      'paternity leave',
      'adoption leave',
    ]),
    requestability: 'PROVIDER_CONFIGURED',
    cohort: 'ALL_STAFF',
    employmentEligibility: 'TENANT_EFFECTIVE_EMPLOYMENT_AND_POLICY_AUTHORITY_REQUIRED',
    unit: 'HOURS',
    balanceSource: 'NOT_BALANCE',
    drawsDownBalance: false,
    negativeBalanceApproval: 'NOT_APPLICABLE',
    evidence: 'EVIDENCE_REQUIRED',
    confidentiality: 'CONFIDENTIAL',
    approval: 'PROVIDER_CONFIGURED',
    independentChecker: 'PROVIDER_CONFIGURED',
    coverage: 'ABSENCE_COVERAGE',
    payroll: 'PROVIDER_CONFIGURED',
    xero: Object.freeze({
      mappingKey: null,
      requiresConfirmedTenantLeaveTypeId: false,
      requiresEmployeePayTemplate: false,
      requiresExactTenantMappingBeforeEnable: false,
    }),
    lifecycle: Object.freeze({
      annualLoading: 'NOT_APPLICABLE',
      cashOut: 'NOT_APPLICABLE',
      finalPay: 'NOT_APPLICABLE',
      publicHolidayIntersection: 'PROVIDER_CONFIGURED',
      portableAuthority: 'NOT_APPLICABLE',
      forecastAccrualSource: 'NOT_APPLICABLE',
    }),
    authority: Object.freeze({
      jurisdiction: 'NOT_APPLICABLE',
      entitlementSource: 'NOT_APPLICABLE',
      portableSchemeSource: 'NOT_APPLICABLE',
      clauseVerificationArtifact: 'NOT_APPLICABLE',
      clauseVerificationArtifactIdentity: 'NOT_APPLICABLE',
      clauseVerificationArtifactVersion: 'NOT_APPLICABLE',
    }),
    providerConfigurableFlags: Object.freeze([
      'requestability',
      'approval',
      'independentChecker',
      'lifecycle.publicHolidayIntersection',
      'payroll',
    ] as const satisfies readonly LeaveTypeProviderConfigurableField[]),
    requiresExactAccrualSourceLink: false,
    push: 'HELD',
    pull: 'HELD',
    // 20271036100034: registry 1.1.0 un-holds parental leave for request
    // capture. push/pull stay HELD and there is no xero mapping key, so no
    // parental absence can reach Xero or pay until that is decided.
    heldReason: null,
  }),
  COMMUNITY_SERVICE: Object.freeze({
    operationalKey: 'COMMUNITY_SERVICE',
    sourceIdentity: 'STATURA_COMMUNITY_SERVICE',
    label: 'Community service leave',
    display: Object.freeze({
      family: 'community_service',
      summaryOnly: true,
      certifyingKey: false,
    }),
    aliases: Object.freeze(['community service leave', 'jury service', 'emergency services leave']),
    requestability: 'PROVIDER_CONFIGURED',
    cohort: 'ALL_STAFF',
    employmentEligibility: 'TENANT_EFFECTIVE_EMPLOYMENT_AND_POLICY_AUTHORITY_REQUIRED',
    unit: 'HOURS',
    balanceSource: 'NOT_BALANCE',
    drawsDownBalance: false,
    negativeBalanceApproval: 'NOT_APPLICABLE',
    evidence: 'EVIDENCE_REQUIRED',
    confidentiality: 'STANDARD',
    approval: 'PROVIDER_CONFIGURED',
    independentChecker: 'NONE',
    coverage: 'ABSENCE_COVERAGE',
    payroll: 'PROVIDER_CONFIGURED',
    xero: Object.freeze({
      mappingKey: null,
      requiresConfirmedTenantLeaveTypeId: false,
      requiresEmployeePayTemplate: false,
      requiresExactTenantMappingBeforeEnable: false,
    }),
    lifecycle: Object.freeze({
      annualLoading: 'NOT_APPLICABLE',
      cashOut: 'NOT_APPLICABLE',
      finalPay: 'NOT_APPLICABLE',
      publicHolidayIntersection: 'PROVIDER_CONFIGURED',
      portableAuthority: 'NOT_APPLICABLE',
      forecastAccrualSource: 'NOT_APPLICABLE',
    }),
    authority: Object.freeze({
      jurisdiction: 'NOT_APPLICABLE',
      entitlementSource: 'NOT_APPLICABLE',
      portableSchemeSource: 'NOT_APPLICABLE',
      clauseVerificationArtifact: 'NOT_APPLICABLE',
      clauseVerificationArtifactIdentity: 'NOT_APPLICABLE',
      clauseVerificationArtifactVersion: 'NOT_APPLICABLE',
    }),
    providerConfigurableFlags: Object.freeze([
      'requestability',
      'approval',
      'lifecycle.publicHolidayIntersection',
      'payroll',
    ] as const satisfies readonly LeaveTypeProviderConfigurableField[]),
    requiresExactAccrualSourceLink: false,
    push: 'HELD',
    pull: 'HELD',
    // 20271036100034: registry 1.1.0 un-holds community service leave for
    // request capture. push/pull stay HELD and there is no xero mapping key,
    // so no community-service absence can reach Xero or pay yet.
    heldReason: null,
  }),
  FAMILY_DOMESTIC_VIOLENCE: Object.freeze({
    operationalKey: 'FAMILY_DOMESTIC_VIOLENCE',
    sourceIdentity: 'STATURA_FAMILY_DOMESTIC_VIOLENCE',
    label: 'Family and domestic violence leave',
    display: Object.freeze({
      family: 'family_domestic_violence',
      summaryOnly: true,
      certifyingKey: false,
    }),
    aliases: Object.freeze([
      'family and domestic violence leave',
      'fdv leave',
      'family violence leave',
    ]),
    requestability: 'PROVIDER_CONFIGURED',
    cohort: 'ALL_STAFF',
    employmentEligibility: 'TENANT_EFFECTIVE_EMPLOYMENT_AND_POLICY_AUTHORITY_REQUIRED',
    unit: 'HOURS',
    balanceSource: 'NOT_BALANCE',
    drawsDownBalance: false,
    negativeBalanceApproval: 'NOT_APPLICABLE',
    evidence: 'EVIDENCE_REQUIRED',
    confidentiality: 'RESTRICTED',
    approval: 'PROVIDER_CONFIGURED',
    independentChecker: 'PROVIDER_CONFIGURED',
    coverage: 'ABSENCE_COVERAGE',
    payroll: 'PROVIDER_CONFIGURED',
    xero: Object.freeze({
      mappingKey: null,
      requiresConfirmedTenantLeaveTypeId: false,
      requiresEmployeePayTemplate: false,
      requiresExactTenantMappingBeforeEnable: false,
    }),
    lifecycle: Object.freeze({
      annualLoading: 'NOT_APPLICABLE',
      cashOut: 'NOT_APPLICABLE',
      finalPay: 'NOT_APPLICABLE',
      publicHolidayIntersection: 'PROVIDER_CONFIGURED',
      portableAuthority: 'NOT_APPLICABLE',
      forecastAccrualSource: 'NOT_APPLICABLE',
    }),
    authority: Object.freeze({
      jurisdiction: 'NOT_APPLICABLE',
      entitlementSource: 'NOT_APPLICABLE',
      portableSchemeSource: 'NOT_APPLICABLE',
      clauseVerificationArtifact: 'NOT_APPLICABLE',
      clauseVerificationArtifactIdentity: 'NOT_APPLICABLE',
      clauseVerificationArtifactVersion: 'NOT_APPLICABLE',
    }),
    providerConfigurableFlags: Object.freeze([
      'requestability',
      'approval',
      'independentChecker',
      'lifecycle.publicHolidayIntersection',
      'payroll',
    ] as const satisfies readonly LeaveTypeProviderConfigurableField[]),
    requiresExactAccrualSourceLink: false,
    push: 'HELD',
    pull: 'HELD',
    heldReason: 'HELD_FDV_PRIVACY_AND_PAYROLL_CONFIGURATION_REQUIRED',
  }),
  PUBLIC_HOLIDAY: Object.freeze({
    operationalKey: 'PUBLIC_HOLIDAY',
    sourceIdentity: 'STATURA_PUBLIC_HOLIDAY',
    label: 'Public holiday',
    display: Object.freeze({
      family: 'public_holiday',
      summaryOnly: true,
      certifyingKey: false,
    }),
    aliases: Object.freeze(['public holiday', 'gazetted public holiday']),
    requestability: 'NEVER',
    cohort: 'ALL_STAFF',
    employmentEligibility: 'NOT_APPLICABLE',
    unit: 'NOT_APPLICABLE',
    balanceSource: 'NOT_BALANCE',
    drawsDownBalance: false,
    negativeBalanceApproval: 'NOT_APPLICABLE',
    evidence: 'NONE',
    confidentiality: 'STANDARD',
    approval: 'SEPARATE_EVENT',
    independentChecker: 'NONE',
    coverage: 'SEPARATE_EVENT',
    payroll: 'SEPARATE_EVENT',
    xero: Object.freeze({
      mappingKey: null,
      requiresConfirmedTenantLeaveTypeId: false,
      requiresEmployeePayTemplate: false,
      requiresExactTenantMappingBeforeEnable: false,
    }),
    lifecycle: Object.freeze({
      annualLoading: 'NOT_APPLICABLE',
      cashOut: 'NOT_APPLICABLE',
      finalPay: 'NOT_APPLICABLE',
      publicHolidayIntersection: 'NOT_APPLICABLE',
      portableAuthority: 'NOT_APPLICABLE',
      forecastAccrualSource: 'NOT_APPLICABLE',
    }),
    authority: Object.freeze({
      jurisdiction: 'NOT_APPLICABLE',
      entitlementSource: 'NOT_APPLICABLE',
      portableSchemeSource: 'NOT_APPLICABLE',
      clauseVerificationArtifact: 'NOT_APPLICABLE',
      clauseVerificationArtifactIdentity: 'NOT_APPLICABLE',
      clauseVerificationArtifactVersion: 'NOT_APPLICABLE',
    }),
    providerConfigurableFlags: Object.freeze([]),
    requiresExactAccrualSourceLink: false,
    push: 'NOT_APPLICABLE',
    pull: 'NOT_APPLICABLE',
    heldReason: null,
  }),
  WORKERS_COMP_NOT_WORKED: Object.freeze({
    operationalKey: 'WORKERS_COMP_NOT_WORKED',
    sourceIdentity: 'STATURA_WORKERS_COMP_NOT_WORKED',
    label: 'Workers compensation (not worked)',
    display: Object.freeze({
      family: 'workers_compensation',
      summaryOnly: true,
      certifyingKey: false,
    }),
    aliases: Object.freeze(['workers compensation not worked', 'workers compensation absence']),
    requestability: 'ALWAYS',
    cohort: 'ALL_STAFF',
    employmentEligibility: 'TENANT_EFFECTIVE_EMPLOYMENT_AND_POLICY_AUTHORITY_REQUIRED',
    unit: 'HOURS',
    balanceSource: 'NOT_BALANCE',
    drawsDownBalance: false,
    negativeBalanceApproval: 'NOT_APPLICABLE',
    evidence: 'EVIDENCE_REQUIRED',
    confidentiality: 'RESTRICTED',
    approval: 'EVIDENCE_REQUIRED',
    independentChecker: 'REQUIRED',
    coverage: 'ABSENCE_COVERAGE',
    payroll: 'PROVIDER_CONFIGURED',
    xero: Object.freeze({
      mappingKey: null,
      requiresConfirmedTenantLeaveTypeId: false,
      requiresEmployeePayTemplate: false,
      requiresExactTenantMappingBeforeEnable: false,
    }),
    lifecycle: Object.freeze({
      annualLoading: 'NOT_APPLICABLE',
      cashOut: 'NOT_APPLICABLE',
      finalPay: 'NOT_APPLICABLE',
      publicHolidayIntersection: 'NOT_APPLICABLE',
      portableAuthority: 'EXTERNAL_AUTHORITY_REQUIRED',
      forecastAccrualSource: 'NOT_APPLICABLE',
    }),
    authority: Object.freeze({
      jurisdiction: 'EXTERNAL_AUTHORITY_REQUIRED',
      entitlementSource: 'EXTERNAL_AUTHORITY_REQUIRED',
      portableSchemeSource: 'EXTERNAL_AUTHORITY_REQUIRED',
      clauseVerificationArtifact: 'REQUIRED',
      clauseVerificationArtifactIdentity: 'REQUIRED',
      clauseVerificationArtifactVersion: 'REQUIRED',
    }),
    providerConfigurableFlags: Object.freeze([
      'payroll',
    ] as const satisfies readonly LeaveTypeProviderConfigurableField[]),
    requiresExactAccrualSourceLink: false,
    push: 'HELD',
    pull: 'HELD',
    heldReason: null,
  }),
  ANNUAL_ADDITIONAL_SHIFTWORKER_WEEK: Object.freeze({
    operationalKey: 'ANNUAL_ADDITIONAL_SHIFTWORKER_WEEK',
    sourceIdentity: 'STATURA_ANNUAL_ADDITIONAL_SHIFTWORKER_WEEK',
    label: 'Additional week of annual leave (shiftworker)',
    display: Object.freeze({
      family: 'annual',
      summaryOnly: true,
      certifyingKey: false,
    }),
    aliases: Object.freeze(['additional week (shiftworker)', 'shiftworker extra week']),
    requestability: 'HELD',
    cohort: 'SHIFTWORKER',
    employmentEligibility: 'TENANT_EFFECTIVE_EMPLOYMENT_AND_POLICY_AUTHORITY_REQUIRED',
    unit: 'HOURS',
    balanceSource: 'ACCRUED',
    drawsDownBalance: true,
    negativeBalanceApproval: 'HELD',
    evidence: 'NONE',
    confidentiality: 'STANDARD',
    approval: 'PROVIDER_CONFIGURED',
    independentChecker: 'NONE',
    coverage: 'ABSENCE_COVERAGE',
    payroll: 'HELD',
    xero: Object.freeze({
      mappingKey: null,
      requiresConfirmedTenantLeaveTypeId: false,
      requiresEmployeePayTemplate: false,
      requiresExactTenantMappingBeforeEnable: true,
    }),
    lifecycle: Object.freeze({
      annualLoading: 'PROVIDER_CONFIGURED',
      cashOut: 'HELD',
      finalPay: 'HELD',
      publicHolidayIntersection: 'PROVIDER_CONFIGURED',
      portableAuthority: 'NOT_APPLICABLE',
      forecastAccrualSource: 'HELD',
    }),
    authority: Object.freeze({
      jurisdiction: 'NOT_APPLICABLE',
      entitlementSource: 'NOT_APPLICABLE',
      portableSchemeSource: 'NOT_APPLICABLE',
      clauseVerificationArtifact: 'NOT_APPLICABLE',
      clauseVerificationArtifactIdentity: 'NOT_APPLICABLE',
      clauseVerificationArtifactVersion: 'NOT_APPLICABLE',
    }),
    providerConfigurableFlags: Object.freeze([
      'approval',
      'lifecycle.annualLoading',
      'lifecycle.publicHolidayIntersection',
    ] as const satisfies readonly LeaveTypeProviderConfigurableField[]),
    requiresExactAccrualSourceLink: false,
    push: 'HELD',
    pull: 'HELD',
    heldReason: 'HELD_ANNUAL_ADDITIONAL_WEEK_CONFIGURATION_REQUIRED',
  }),
  TOIL: Object.freeze({
    operationalKey: 'TOIL',
    sourceIdentity: 'STATURA_TOIL',
    label: 'Time off in lieu',
    display: Object.freeze({
      family: 'toil',
      summaryOnly: true,
      certifyingKey: false,
    }),
    aliases: Object.freeze(['toil', 'time off in lieu']),
    requestability: 'HELD',
    cohort: 'PROVIDER_CONFIGURED',
    employmentEligibility: 'TENANT_EFFECTIVE_EMPLOYMENT_AND_POLICY_AUTHORITY_REQUIRED',
    unit: 'HOURS',
    balanceSource: 'ACCRUED',
    drawsDownBalance: true,
    negativeBalanceApproval: 'HELD',
    evidence: 'NONE',
    confidentiality: 'STANDARD',
    approval: 'PROVIDER_CONFIGURED',
    independentChecker: 'NONE',
    coverage: 'ABSENCE_COVERAGE',
    payroll: 'HELD',
    xero: Object.freeze({
      mappingKey: null,
      requiresConfirmedTenantLeaveTypeId: false,
      requiresEmployeePayTemplate: false,
      requiresExactTenantMappingBeforeEnable: true,
    }),
    lifecycle: Object.freeze({
      annualLoading: 'NOT_APPLICABLE',
      cashOut: 'HELD',
      finalPay: 'HELD',
      publicHolidayIntersection: 'PROVIDER_CONFIGURED',
      portableAuthority: 'NOT_APPLICABLE',
      forecastAccrualSource: 'HELD',
    }),
    authority: Object.freeze({
      jurisdiction: 'NOT_APPLICABLE',
      entitlementSource: 'NOT_APPLICABLE',
      portableSchemeSource: 'NOT_APPLICABLE',
      clauseVerificationArtifact: 'NOT_APPLICABLE',
      clauseVerificationArtifactIdentity: 'NOT_APPLICABLE',
      clauseVerificationArtifactVersion: 'NOT_APPLICABLE',
    }),
    providerConfigurableFlags: Object.freeze([
      'cohort',
      'approval',
      'lifecycle.publicHolidayIntersection',
    ] as const satisfies readonly LeaveTypeProviderConfigurableField[]),
    requiresExactAccrualSourceLink: true,
    push: 'HELD',
    pull: 'HELD',
    heldReason: 'HELD_TOIL_EXACT_ACCRUAL_SOURCE_REQUIRED',
  }),
  ADO: Object.freeze({
    operationalKey: 'ADO',
    sourceIdentity: 'STATURA_ADO',
    label: 'Accrued day off',
    display: Object.freeze({
      family: 'ado',
      summaryOnly: true,
      certifyingKey: false,
    }),
    aliases: Object.freeze(['ado', 'accrued day off']),
    requestability: 'HELD',
    cohort: 'PROVIDER_CONFIGURED',
    employmentEligibility: 'TENANT_EFFECTIVE_EMPLOYMENT_AND_POLICY_AUTHORITY_REQUIRED',
    unit: 'HOURS',
    balanceSource: 'ACCRUED',
    drawsDownBalance: true,
    negativeBalanceApproval: 'HELD',
    evidence: 'NONE',
    confidentiality: 'STANDARD',
    approval: 'PROVIDER_CONFIGURED',
    independentChecker: 'NONE',
    coverage: 'ABSENCE_COVERAGE',
    payroll: 'HELD',
    xero: Object.freeze({
      mappingKey: null,
      requiresConfirmedTenantLeaveTypeId: false,
      requiresEmployeePayTemplate: false,
      requiresExactTenantMappingBeforeEnable: true,
    }),
    lifecycle: Object.freeze({
      annualLoading: 'NOT_APPLICABLE',
      cashOut: 'HELD',
      finalPay: 'HELD',
      publicHolidayIntersection: 'PROVIDER_CONFIGURED',
      portableAuthority: 'NOT_APPLICABLE',
      forecastAccrualSource: 'HELD',
    }),
    authority: Object.freeze({
      jurisdiction: 'NOT_APPLICABLE',
      entitlementSource: 'NOT_APPLICABLE',
      portableSchemeSource: 'NOT_APPLICABLE',
      clauseVerificationArtifact: 'NOT_APPLICABLE',
      clauseVerificationArtifactIdentity: 'NOT_APPLICABLE',
      clauseVerificationArtifactVersion: 'NOT_APPLICABLE',
    }),
    providerConfigurableFlags: Object.freeze([
      'cohort',
      'approval',
      'lifecycle.publicHolidayIntersection',
    ] as const satisfies readonly LeaveTypeProviderConfigurableField[]),
    requiresExactAccrualSourceLink: true,
    push: 'HELD',
    pull: 'HELD',
    heldReason: 'HELD_ADO_EXACT_ACCRUAL_SOURCE_REQUIRED',
  }),
  CASH_OUT: Object.freeze({
    operationalKey: 'CASH_OUT',
    sourceIdentity: 'STATURA_CASH_OUT',
    label: 'Leave cash-out',
    display: Object.freeze({
      family: 'cash_out',
      summaryOnly: true,
      certifyingKey: false,
    }),
    aliases: Object.freeze(['leave cash-out', 'annual leave cash-out', 'cash out leave']),
    requestability: 'NEVER',
    cohort: 'ALL_STAFF',
    employmentEligibility: 'TENANT_EFFECTIVE_EMPLOYMENT_AND_POLICY_AUTHORITY_REQUIRED',
    unit: 'HOURS',
    balanceSource: 'NOT_BALANCE',
    drawsDownBalance: false,
    negativeBalanceApproval: 'NOT_APPLICABLE',
    evidence: 'EVIDENCE_REQUIRED',
    confidentiality: 'STANDARD',
    approval: 'SEPARATE_EVENT',
    independentChecker: 'NONE',
    coverage: 'SEPARATE_EVENT',
    payroll: 'SEPARATE_EVENT',
    xero: Object.freeze({
      mappingKey: null,
      requiresConfirmedTenantLeaveTypeId: false,
      requiresEmployeePayTemplate: false,
      requiresExactTenantMappingBeforeEnable: false,
    }),
    lifecycle: Object.freeze({
      annualLoading: 'NOT_APPLICABLE',
      cashOut: 'NOT_APPLICABLE',
      finalPay: 'NOT_APPLICABLE',
      publicHolidayIntersection: 'NOT_APPLICABLE',
      portableAuthority: 'NOT_APPLICABLE',
      forecastAccrualSource: 'NOT_APPLICABLE',
    }),
    authority: Object.freeze({
      jurisdiction: 'NOT_APPLICABLE',
      entitlementSource: 'NOT_APPLICABLE',
      portableSchemeSource: 'NOT_APPLICABLE',
      clauseVerificationArtifact: 'NOT_APPLICABLE',
      clauseVerificationArtifactIdentity: 'NOT_APPLICABLE',
      clauseVerificationArtifactVersion: 'NOT_APPLICABLE',
    }),
    providerConfigurableFlags: Object.freeze([
      'push',
    ] as const satisfies readonly LeaveTypeProviderConfigurableField[]),
    requiresExactAccrualSourceLink: false,
    push: 'PROVIDER_CONFIGURED',
    pull: 'NOT_APPLICABLE',
    heldReason: null,
  }),
  TERMINATION_PAYOUT: Object.freeze({
    operationalKey: 'TERMINATION_PAYOUT',
    sourceIdentity: 'STATURA_TERMINATION_PAYOUT',
    label: 'Termination leave payout',
    display: Object.freeze({
      family: 'termination_payout',
      summaryOnly: true,
      certifyingKey: false,
    }),
    aliases: Object.freeze([
      'termination leave payout',
      'termination payout',
      'annual leave on termination',
    ]),
    requestability: 'NEVER',
    cohort: 'ALL_STAFF',
    employmentEligibility: 'TENANT_EFFECTIVE_EMPLOYMENT_AND_POLICY_AUTHORITY_REQUIRED',
    unit: 'HOURS',
    balanceSource: 'NOT_BALANCE',
    drawsDownBalance: false,
    negativeBalanceApproval: 'NOT_APPLICABLE',
    evidence: 'NONE',
    confidentiality: 'STANDARD',
    approval: 'SEPARATE_EVENT',
    independentChecker: 'NONE',
    coverage: 'SEPARATE_EVENT',
    payroll: 'SEPARATE_EVENT',
    xero: Object.freeze({
      mappingKey: null,
      requiresConfirmedTenantLeaveTypeId: false,
      requiresEmployeePayTemplate: false,
      requiresExactTenantMappingBeforeEnable: false,
    }),
    lifecycle: Object.freeze({
      annualLoading: 'NOT_APPLICABLE',
      cashOut: 'NOT_APPLICABLE',
      finalPay: 'NOT_APPLICABLE',
      publicHolidayIntersection: 'NOT_APPLICABLE',
      portableAuthority: 'NOT_APPLICABLE',
      forecastAccrualSource: 'NOT_APPLICABLE',
    }),
    authority: Object.freeze({
      jurisdiction: 'NOT_APPLICABLE',
      entitlementSource: 'NOT_APPLICABLE',
      portableSchemeSource: 'NOT_APPLICABLE',
      clauseVerificationArtifact: 'NOT_APPLICABLE',
      clauseVerificationArtifactIdentity: 'NOT_APPLICABLE',
      clauseVerificationArtifactVersion: 'NOT_APPLICABLE',
    }),
    providerConfigurableFlags: Object.freeze([
      'push',
    ] as const satisfies readonly LeaveTypeProviderConfigurableField[]),
    requiresExactAccrualSourceLink: false,
    push: 'PROVIDER_CONFIGURED',
    pull: 'NOT_APPLICABLE',
    heldReason: null,
  }),
}) satisfies { readonly [K in LeaveTypeOperationalKey]: LeaveTypeRegistryRow };

export type LeaveTypeRegistryErrorCode =
  | 'UNKNOWN_OPERATIONAL_KEY'
  | 'DISPLAY_FAMILY_NOT_OPERATIONAL'
  | 'UNKNOWN_SOURCE_IDENTITY'
  | 'UNKNOWN_IMPORT_ALIAS'
  | 'DUPLICATE_OPERATIONAL_KEY'
  | 'MISSING_OPERATIONAL_KEY'
  | 'DUPLICATE_SOURCE_IDENTITY'
  | 'DUPLICATE_ALIAS'
  | 'DUPLICATE_XERO_MAPPING_KEY'
  | 'INVARIANT_VIOLATION'
  | 'REGISTRY_DRIFT';

export type LeaveTypeRegistryIssue = {
  readonly code: LeaveTypeRegistryErrorCode;
  readonly message: string;
  readonly operationalKey?: string;
  readonly value?: string;
};

export type LeaveTypeRegistryValidationResult = {
  readonly ok: boolean;
  readonly issues: readonly LeaveTypeRegistryIssue[];
};

export class LeaveTypeRegistryError extends Error {
  readonly code: LeaveTypeRegistryErrorCode;
  readonly operationalKey?: string;
  readonly value?: string;

  constructor(
    code: LeaveTypeRegistryErrorCode,
    message: string,
    extras?: { readonly operationalKey?: string; readonly value?: string },
  ) {
    super(message);
    this.name = 'LeaveTypeRegistryError';
    this.code = code;
    if (extras?.operationalKey !== undefined) {
      this.operationalKey = extras.operationalKey;
    }
    if (extras?.value !== undefined) {
      this.value = extras.value;
    }
  }
}

const KNOWN_OPERATIONAL_KEYS: readonly LeaveTypeOperationalKey[] = Object.freeze([
  'ANNUAL',
  'PERSONAL_GENERAL_STAFF',
  'PERSONAL_NURSES',
  'CARERS_EXTRA_GENERAL_STAFF',
  'LONG_SERVICE',
  'COMPASSIONATE',
  'UNPAID',
  'PARENTAL',
  'COMMUNITY_SERVICE',
  'FAMILY_DOMESTIC_VIOLENCE',
  'PUBLIC_HOLIDAY',
  'WORKERS_COMP_NOT_WORKED',
  'ANNUAL_ADDITIONAL_SHIFTWORKER_WEEK',
  'TOIL',
  'ADO',
  'CASH_OUT',
  'TERMINATION_PAYOUT',
]);

const KNOWN_OPERATIONAL_KEY_SET: ReadonlySet<string> = new Set(KNOWN_OPERATIONAL_KEYS);

const LEAVE_TYPE_SUPPORTED_XERO_MAPPING_KEY_SET: ReadonlySet<string> = new Set(
  LEAVE_TYPE_SUPPORTED_XERO_MAPPING_KEYS,
);

const LEAVE_TYPE_EXPECTED_XERO_MAPPING_BY_OPERATIONAL_KEY: {
  readonly [K in LeaveTypeOperationalKey]: LeaveTypeXeroMappingKey;
} = Object.freeze({
  ANNUAL: 'ANNUAL',
  PERSONAL_GENERAL_STAFF: 'PERSONAL_GENERAL_STAFF',
  PERSONAL_NURSES: 'PERSONAL_NURSES',
  CARERS_EXTRA_GENERAL_STAFF: 'CARERS_EXTRA_GENERAL_STAFF',
  LONG_SERVICE: 'LONG_SERVICE',
  COMPASSIONATE: 'COMPASSIONATE',
  UNPAID: 'UNPAID',
  PARENTAL: null,
  COMMUNITY_SERVICE: null,
  FAMILY_DOMESTIC_VIOLENCE: null,
  PUBLIC_HOLIDAY: null,
  WORKERS_COMP_NOT_WORKED: null,
  ANNUAL_ADDITIONAL_SHIFTWORKER_WEEK: null,
  TOIL: null,
  ADO: null,
  CASH_OUT: null,
  TERMINATION_PAYOUT: null,
});

const LEAVE_TYPE_PROVIDER_CONFIGURABLE_FIELDS = Object.freeze([
  'requestability',
  'cohort',
  'negativeBalanceApproval',
  'approval',
  'independentChecker',
  'payroll',
  'push',
  'pull',
  'lifecycle.annualLoading',
  'lifecycle.cashOut',
  'lifecycle.finalPay',
  'lifecycle.publicHolidayIntersection',
  'lifecycle.portableAuthority',
  'lifecycle.forecastAccrualSource',
] as const satisfies readonly LeaveTypeProviderConfigurableField[]);

const LEAVE_TYPE_PROVIDER_CONFIGURABLE_FIELD_SET: ReadonlySet<string> = new Set(
  LEAVE_TYPE_PROVIDER_CONFIGURABLE_FIELDS,
);

const PROVIDER_CONFIGURABLE_FIELD_RESOLVERS: {
  readonly [K in LeaveTypeProviderConfigurableField]: (row: LeaveTypeRegistryRow) => unknown;
} = Object.freeze({
  requestability: (row) => row.requestability,
  cohort: (row) => row.cohort,
  negativeBalanceApproval: (row) => row.negativeBalanceApproval,
  approval: (row) => row.approval,
  independentChecker: (row) => row.independentChecker,
  payroll: (row) => row.payroll,
  push: (row) => row.push,
  pull: (row) => row.pull,
  'lifecycle.annualLoading': (row) => row.lifecycle.annualLoading,
  'lifecycle.cashOut': (row) => row.lifecycle.cashOut,
  'lifecycle.finalPay': (row) => row.lifecycle.finalPay,
  'lifecycle.publicHolidayIntersection': (row) => row.lifecycle.publicHolidayIntersection,
  'lifecycle.portableAuthority': (row) => row.lifecycle.portableAuthority,
  'lifecycle.forecastAccrualSource': (row) => row.lifecycle.forecastAccrualSource,
});

const KNOWN_EMPLOYMENT_ELIGIBILITY_VALUES = Object.freeze([
  'TENANT_EFFECTIVE_EMPLOYMENT_AND_POLICY_AUTHORITY_REQUIRED',
  'NOT_APPLICABLE',
] as const satisfies readonly LeaveTypeEmploymentEligibility[]);

const KNOWN_EMPLOYMENT_ELIGIBILITY_SET: ReadonlySet<string> = new Set(
  KNOWN_EMPLOYMENT_ELIGIBILITY_VALUES,
);

const SEPARATE_EVENT_OR_CASE_KEYS: ReadonlySet<string> = new Set(['PUBLIC_HOLIDAY']);

function isLeaveTypeEmploymentEligibility(value: unknown): value is LeaveTypeEmploymentEligibility {
  return typeof value === 'string' && KNOWN_EMPLOYMENT_ELIGIBILITY_SET.has(value);
}

export function normalizeLeaveTypeImportAlias(raw: string): string {
  return String(raw)
    .trim()
    .toLowerCase()
    .replace(/['\u2018\u2019\u201B\u2032\u02BC\uFF07]/g, "'")
    .replace(/\s+/g, ' ');
}

function deepFreeze<T>(value: T, seen: WeakSet<object> = new WeakSet()): T {
  if (value === null || typeof value !== 'object') {
    return value;
  }
  if (seen.has(value)) {
    return value;
  }
  seen.add(value);
  if (!Object.isFrozen(value)) {
    Object.freeze(value);
  }
  for (const nested of Object.values(value as Record<string, unknown>)) {
    deepFreeze(nested, seen);
  }
  return value;
}

export function serializeLeaveTypeRegistryCanonical(value: unknown): string {
  const walk = (node: unknown): unknown => {
    if (node === null || typeof node !== 'object') {
      return node;
    }
    if (Array.isArray(node)) {
      return node.map((item) => walk(item));
    }
    const keys = Object.keys(node as Record<string, unknown>).sort();
    const next: Record<string, unknown> = {};
    for (const key of keys) {
      next[key] = walk((node as Record<string, unknown>)[key]);
    }
    return next;
  };
  return JSON.stringify(walk(value));
}

export function fnv1a32Hex(input: string): string {
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

export const LEAVE_TYPE_REGISTRY_DIGEST = fnv1a32Hex(
  serializeLeaveTypeRegistryCanonical(Object.values(LEAVE_TYPE_REGISTRY)),
);

// Migration 20271044900003 publishes this digest for registry 1.3.0.
export const LEAVE_TYPE_REGISTRY_EXPECTED_DIGEST = '6dc9eb44' as const;

export const LEAVE_TYPE_REGISTRY_REQUIRED_DRIFT_SURFACES = Object.freeze([
  'web',
  'mobile',
  'database',
  'payroll',
  'reconciliation',
] as const);

function freezeValidationResult(
  issues: LeaveTypeRegistryIssue[],
): LeaveTypeRegistryValidationResult {
  const frozenIssues = Object.freeze(issues.map((issue) => Object.freeze({ ...issue })));
  return Object.freeze({
    ok: frozenIssues.length === 0,
    issues: frozenIssues,
  });
}

function displayFamiliesFromRows(rows: readonly LeaveTypeRegistryRow[]): ReadonlySet<string> {
  const families = new Set<string>();
  for (const row of rows) {
    families.add(row.display.family);
    families.add(row.display.family.toUpperCase());
    families.add(row.display.family.toLowerCase());
  }
  return families;
}

export function validateLeaveTypeRegistry(
  rows: readonly LeaveTypeRegistryRow[] = Object.values(LEAVE_TYPE_REGISTRY),
  expectedDigest?: string,
): LeaveTypeRegistryValidationResult {
  const issues: LeaveTypeRegistryIssue[] = [];
  const operationalSeen = new Map<string, number>();
  const sourceSeen = new Map<string, number>();
  const aliasSeen = new Map<string, string>();
  const xeroSeen = new Map<string, string>();

  for (const row of rows) {
    const key = row.operationalKey;
    operationalSeen.set(key, (operationalSeen.get(key) ?? 0) + 1);
    sourceSeen.set(row.sourceIdentity, (sourceSeen.get(row.sourceIdentity) ?? 0) + 1);

    if (!KNOWN_OPERATIONAL_KEY_SET.has(key)) {
      issues.push({
        code: 'INVARIANT_VIOLATION',
        message: `Unknown registry operational key ${key}.`,
        operationalKey: key,
        value: key,
      });
    }

    const expectedSourceIdentity = `STATURA_${key}`;
    if (row.sourceIdentity !== expectedSourceIdentity) {
      issues.push({
        code: 'INVARIANT_VIOLATION',
        message: `sourceIdentity must be exactly ${expectedSourceIdentity}.`,
        operationalKey: key,
        value: row.sourceIdentity,
      });
    }

    const configurableFlags = row.providerConfigurableFlags as readonly string[];
    const configurableSeen = new Set<string>();
    for (const flag of configurableFlags) {
      if (configurableSeen.has(flag)) {
        issues.push({
          code: 'INVARIANT_VIOLATION',
          message: `Duplicate providerConfigurableFlags entry "${flag}".`,
          operationalKey: key,
          value: flag,
        });
        continue;
      }
      configurableSeen.add(flag);
      if (!LEAVE_TYPE_PROVIDER_CONFIGURABLE_FIELD_SET.has(flag)) {
        issues.push({
          code: 'INVARIANT_VIOLATION',
          message: `Unknown providerConfigurableFlags entry "${flag}".`,
          operationalKey: key,
          value: flag,
        });
        continue;
      }
      const resolved =
        PROVIDER_CONFIGURABLE_FIELD_RESOLVERS[flag as LeaveTypeProviderConfigurableField](row);
      if (resolved !== 'PROVIDER_CONFIGURED') {
        issues.push({
          code: 'INVARIANT_VIOLATION',
          message: `providerConfigurableFlags entry "${flag}" must resolve to PROVIDER_CONFIGURED.`,
          operationalKey: key,
          value: resolved === undefined || resolved === null ? flag : String(resolved),
        });
      }
    }

    for (const field of LEAVE_TYPE_PROVIDER_CONFIGURABLE_FIELDS) {
      const resolved = PROVIDER_CONFIGURABLE_FIELD_RESOLVERS[field](row);
      if (resolved === 'PROVIDER_CONFIGURED' && !configurableSeen.has(field)) {
        issues.push({
          code: 'INVARIANT_VIOLATION',
          message: `Field "${field}" is PROVIDER_CONFIGURED but missing from providerConfigurableFlags.`,
          operationalKey: key,
          value: field,
        });
      }
    }

    const eligibility: unknown = (row as { employmentEligibility?: unknown }).employmentEligibility;
    if (!isLeaveTypeEmploymentEligibility(eligibility)) {
      issues.push({
        code: 'INVARIANT_VIOLATION',
        message:
          'employmentEligibility is missing or unknown; cohort is classification only and must never imply entitlement.',
        operationalKey: key,
        value: eligibility === undefined || eligibility === null ? undefined : String(eligibility),
      });
    } else if (SEPARATE_EVENT_OR_CASE_KEYS.has(key)) {
      if (eligibility !== 'NOT_APPLICABLE') {
        issues.push({
          code: 'INVARIANT_VIOLATION',
          message: `${key} employmentEligibility must be NOT_APPLICABLE because it is a separate event/case, not an ordinary leave liability.`,
          operationalKey: key,
          value: eligibility,
        });
      }
    } else if (eligibility !== 'TENANT_EFFECTIVE_EMPLOYMENT_AND_POLICY_AUTHORITY_REQUIRED') {
      issues.push({
        code: 'INVARIANT_VIOLATION',
        message:
          'NOT_APPLICABLE employmentEligibility is not allowed on ordinary operational leave; tenant effective employment and policy authority is required.',
        operationalKey: key,
        value: eligibility,
      });
    }

    if (row.display.summaryOnly !== true || row.display.certifyingKey !== false) {
      issues.push({
        code: 'INVARIANT_VIOLATION',
        message:
          'Display must be summaryOnly true and certifyingKey false; display families are not operational.',
        operationalKey: key,
      });
    }

    const mappingNull = row.xero.mappingKey === null;
    const bothFalse =
      row.xero.requiresConfirmedTenantLeaveTypeId === false &&
      row.xero.requiresEmployeePayTemplate === false;
    const bothTrue =
      row.xero.requiresConfirmedTenantLeaveTypeId === true &&
      row.xero.requiresEmployeePayTemplate === true;
    if (mappingNull !== bothFalse || !mappingNull !== bothTrue) {
      issues.push({
        code: 'INVARIANT_VIOLATION',
        message:
          'Xero mappingKey must be null iff both requirement flags are false, and non-null iff both are true.',
        operationalKey: key,
      });
    }

    const accrued = row.balanceSource === 'ACCRUED';
    if (accrued !== row.drawsDownBalance) {
      issues.push({
        code: 'INVARIANT_VIOLATION',
        message: 'balanceSource ACCRUED if and only if drawsDownBalance is true.',
        operationalKey: key,
      });
    }

    if (row.requestability === 'HELD') {
      if (row.payroll !== 'HELD' || row.push !== 'HELD' || row.pull !== 'HELD') {
        issues.push({
          code: 'INVARIANT_VIOLATION',
          message: 'HELD requestability requires payroll, push, and pull to be HELD.',
          operationalKey: key,
        });
      }
    }

    const requiresExactTenantMappingBeforeEnable =
      row.xero.mappingKey !== null ||
      key === 'ANNUAL_ADDITIONAL_SHIFTWORKER_WEEK' ||
      key === 'TOIL' ||
      key === 'ADO';
    if (
      row.xero.requiresExactTenantMappingBeforeEnable !== requiresExactTenantMappingBeforeEnable
    ) {
      issues.push({
        code: 'INVARIANT_VIOLATION',
        message:
          'requiresExactTenantMappingBeforeEnable must be true for mapped rows and held additional-week/TOIL/ADO, otherwise false.',
        operationalKey: key,
      });
    }

    if (key === 'COMPASSIONATE') {
      const compassionateOk =
        row.payroll === 'HELD' &&
        row.push === 'HELD' &&
        row.pull === 'HELD' &&
        row.balanceSource === 'NOT_BALANCE' &&
        row.drawsDownBalance === false &&
        row.lifecycle.finalPay === 'NOT_APPLICABLE' &&
        row.evidence === 'EVIDENCE_REQUIRED' &&
        row.confidentiality !== 'STANDARD';
      if (!compassionateOk) {
        issues.push({
          code: 'INVARIANT_VIOLATION',
          message:
            'Compassionate leave must hold payroll and sync, have no balance or draw-down, keep finalPay NOT_APPLICABLE, require evidence, and must not use STANDARD confidentiality.',
          operationalKey: key,
        });
      }
    }

    if (key === 'LONG_SERVICE') {
      const lslAuthorityOk =
        row.requestability === 'ALWAYS' &&
        row.payroll === 'PROVIDER_CONFIGURED' &&
        row.push === 'HELD' &&
        row.pull === 'HELD' &&
        row.evidence === 'EVIDENCE_REQUIRED' &&
        row.lifecycle.cashOut === 'HELD' &&
        row.lifecycle.finalPay === 'HELD' &&
        row.lifecycle.forecastAccrualSource === 'HELD' &&
        row.lifecycle.portableAuthority === 'EXTERNAL_AUTHORITY_REQUIRED' &&
        row.authority.jurisdiction === 'EXTERNAL_AUTHORITY_REQUIRED' &&
        row.authority.entitlementSource === 'EXTERNAL_AUTHORITY_REQUIRED' &&
        row.authority.portableSchemeSource === 'EXTERNAL_AUTHORITY_REQUIRED' &&
        row.authority.clauseVerificationArtifact === 'REQUIRED' &&
        row.authority.clauseVerificationArtifactIdentity === 'REQUIRED' &&
        row.authority.clauseVerificationArtifactVersion === 'REQUIRED' &&
        row.heldReason === null;
      if (!lslAuthorityOk) {
        issues.push({
          code: 'INVARIANT_VIOLATION',
          message:
            'Long service leave request capture and provider payroll must be enabled while sync, cash-out, final pay, forecast accrual, and portable authority remain held.',
          operationalKey: key,
        });
      }
    }

    if (
      key === 'PERSONAL_GENERAL_STAFF' ||
      key === 'PERSONAL_NURSES' ||
      key === 'CARERS_EXTRA_GENERAL_STAFF'
    ) {
      const personalFinalPayOk =
        row.lifecycle.finalPay === 'HELD' &&
        row.requestability !== 'HELD' &&
        row.payroll !== 'HELD';
      if (!personalFinalPayOk) {
        issues.push({
          code: 'INVARIANT_VIOLATION',
          message:
            'Personal and extra carer leave must hold final-pay treatment as authority-dependent without holding ordinary request or payroll.',
          operationalKey: key,
        });
      }
    }

    if (key === 'FAMILY_DOMESTIC_VIOLENCE') {
      const fdvOk =
        row.confidentiality === 'RESTRICTED' &&
        row.evidence === 'EVIDENCE_REQUIRED' &&
        row.push === 'HELD' &&
        row.pull === 'HELD';
      if (!fdvOk) {
        issues.push({
          code: 'INVARIANT_VIOLATION',
          message:
            'Family and domestic violence leave must be RESTRICTED, require evidence, and hold push and pull.',
          operationalKey: key,
        });
      }
    }

    if (key === 'PUBLIC_HOLIDAY') {
      const holidayOk =
        row.requestability === 'NEVER' &&
        row.unit === 'NOT_APPLICABLE' &&
        row.approval === 'SEPARATE_EVENT' &&
        row.coverage === 'SEPARATE_EVENT' &&
        row.payroll === 'SEPARATE_EVENT' &&
        row.push === 'NOT_APPLICABLE' &&
        row.pull === 'NOT_APPLICABLE' &&
        row.xero.mappingKey === null &&
        row.balanceSource === 'NOT_BALANCE' &&
        row.drawsDownBalance === false;
      if (!holidayOk) {
        issues.push({
          code: 'INVARIANT_VIOLATION',
          message:
            'Public Holiday must be a never-requestable separate event with no unit, no Xero mapping, no balance draw-down, and no push or pull.',
          operationalKey: key,
        });
      }
    }

    if (key === 'WORKERS_COMP_NOT_WORKED') {
      const wcOk =
        row.requestability === 'ALWAYS' &&
        row.employmentEligibility === 'TENANT_EFFECTIVE_EMPLOYMENT_AND_POLICY_AUTHORITY_REQUIRED' &&
        row.unit === 'HOURS' &&
        row.approval === 'EVIDENCE_REQUIRED' &&
        row.coverage === 'ABSENCE_COVERAGE' &&
        row.payroll === 'PROVIDER_CONFIGURED' &&
        row.push === 'HELD' &&
        row.pull === 'HELD' &&
        row.xero.mappingKey === null &&
        row.balanceSource === 'NOT_BALANCE' &&
        row.drawsDownBalance === false &&
        row.lifecycle.portableAuthority === 'EXTERNAL_AUTHORITY_REQUIRED' &&
        row.authority.jurisdiction === 'EXTERNAL_AUTHORITY_REQUIRED' &&
        row.authority.entitlementSource === 'EXTERNAL_AUTHORITY_REQUIRED' &&
        row.authority.portableSchemeSource === 'EXTERNAL_AUTHORITY_REQUIRED' &&
        row.authority.clauseVerificationArtifact === 'REQUIRED' &&
        row.authority.clauseVerificationArtifactIdentity === 'REQUIRED' &&
        row.authority.clauseVerificationArtifactVersion === 'REQUIRED';
      if (!wcOk) {
        issues.push({
          code: 'INVARIANT_VIOLATION',
          message:
            'Workers compensation not worked must be an evidence-backed hours request with no Xero mapping or balance draw-down; provider payroll is enabled while push and pull stay held.',
          operationalKey: key,
        });
      }
    }

    const requiresClauseArtifactIdentityAndVersion =
      key === 'LONG_SERVICE' || key === 'WORKERS_COMP_NOT_WORKED';
    const identityRequirement = (row.authority as { clauseVerificationArtifactIdentity?: unknown })
      .clauseVerificationArtifactIdentity;
    const versionRequirement = (row.authority as { clauseVerificationArtifactVersion?: unknown })
      .clauseVerificationArtifactVersion;
    if (requiresClauseArtifactIdentityAndVersion) {
      if (identityRequirement !== 'REQUIRED' || versionRequirement !== 'REQUIRED') {
        issues.push({
          code: 'INVARIANT_VIOLATION',
          message:
            'clauseVerificationArtifactIdentity and clauseVerificationArtifactVersion must be REQUIRED for long service leave and workers compensation; no authority identity or version value is stored here.',
          operationalKey: key,
          value:
            identityRequirement === undefined || identityRequirement === null
              ? 'missing'
              : String(identityRequirement),
        });
      }
    } else if (
      identityRequirement !== 'NOT_APPLICABLE' ||
      versionRequirement !== 'NOT_APPLICABLE'
    ) {
      issues.push({
        code: 'INVARIANT_VIOLATION',
        message:
          'clauseVerificationArtifactIdentity and clauseVerificationArtifactVersion must be NOT_APPLICABLE except for long service leave and workers compensation.',
        operationalKey: key,
        value:
          identityRequirement === undefined || identityRequirement === null
            ? 'missing'
            : String(identityRequirement),
      });
    }

    if (key === 'TOIL' || key === 'ADO') {
      const heldLinkOk =
        row.requiresExactAccrualSourceLink === true &&
        row.requestability === 'HELD' &&
        row.negativeBalanceApproval === 'HELD' &&
        row.payroll === 'HELD' &&
        row.push === 'HELD' &&
        row.pull === 'HELD' &&
        row.lifecycle.cashOut === 'HELD' &&
        row.lifecycle.finalPay === 'HELD' &&
        row.lifecycle.forecastAccrualSource === 'HELD' &&
        row.xero.requiresExactTenantMappingBeforeEnable === true;
      if (!heldLinkOk) {
        issues.push({
          code: 'INVARIANT_VIOLATION',
          message: `${key} must require an exact accrual source link and remain HELD for request, negative-balance approval, payroll, sync, cash-out, final pay, and forecast accrual.`,
          operationalKey: key,
        });
      }
    }

    if (key === 'ANNUAL_ADDITIONAL_SHIFTWORKER_WEEK') {
      const extraWeekOk =
        row.balanceSource === 'ACCRUED' &&
        row.drawsDownBalance === true &&
        row.requestability === 'HELD' &&
        row.negativeBalanceApproval === 'HELD' &&
        row.payroll === 'HELD' &&
        row.push === 'HELD' &&
        row.pull === 'HELD' &&
        row.lifecycle.cashOut === 'HELD' &&
        row.lifecycle.finalPay === 'HELD' &&
        row.lifecycle.forecastAccrualSource === 'HELD' &&
        row.xero.mappingKey === null &&
        row.xero.requiresExactTenantMappingBeforeEnable === true;
      if (!extraWeekOk) {
        issues.push({
          code: 'INVARIANT_VIOLATION',
          message:
            'Additional week of annual leave must be accrued, draw down, remain HELD for request, negative-balance approval, payroll, sync, cash-out, final pay, and forecast accrual, and require an exact tenant Xero mapping before enable even with a null global mapping key.',
          operationalKey: key,
        });
      }
    }

    for (const alias of row.aliases) {
      const normalized = normalizeLeaveTypeImportAlias(alias);
      const prior = aliasSeen.get(normalized);
      if (prior !== undefined) {
        issues.push({
          code: 'DUPLICATE_ALIAS',
          message: `Duplicate normalized import alias "${normalized}".`,
          operationalKey: key,
          value: normalized,
        });
      } else {
        aliasSeen.set(normalized, key);
      }
    }

    if (KNOWN_OPERATIONAL_KEY_SET.has(key)) {
      const expectedMappingKey =
        LEAVE_TYPE_EXPECTED_XERO_MAPPING_BY_OPERATIONAL_KEY[key as LeaveTypeOperationalKey];
      const actualMappingKey = (row.xero as { mappingKey?: unknown }).mappingKey;
      if (actualMappingKey !== expectedMappingKey) {
        issues.push({
          code: 'INVARIANT_VIOLATION',
          message: `Xero mappingKey must be exactly the expected association for operational key ${key}.`,
          operationalKey: key,
          value:
            actualMappingKey === undefined || actualMappingKey === null
              ? 'null'
              : String(actualMappingKey),
        });
      }
    }

    if (row.xero.mappingKey !== null) {
      const mappingKey = row.xero.mappingKey as string;
      if (!LEAVE_TYPE_SUPPORTED_XERO_MAPPING_KEY_SET.has(mappingKey)) {
        issues.push({
          code: 'INVARIANT_VIOLATION',
          message: `Unsupported non-null Xero mapping key ${mappingKey}.`,
          operationalKey: key,
          value: mappingKey,
        });
      }
      const prior = xeroSeen.get(mappingKey);
      if (prior !== undefined) {
        issues.push({
          code: 'DUPLICATE_XERO_MAPPING_KEY',
          message: `Duplicate Xero mapping key ${mappingKey}.`,
          operationalKey: key,
          value: mappingKey,
        });
      } else {
        xeroSeen.set(mappingKey, key);
      }
    }
  }

  const expectedXeroMappingKeys = [...LEAVE_TYPE_SUPPORTED_XERO_MAPPING_KEYS].slice().sort();
  const presentXeroMappingKeys = [...xeroSeen.keys()].sort();
  const presentXeroSet: ReadonlySet<string> = new Set(presentXeroMappingKeys);
  const expectedXeroSet: ReadonlySet<string> = new Set(expectedXeroMappingKeys);
  const missingXeroKeys = expectedXeroMappingKeys.filter((key) => !presentXeroSet.has(key));
  const extraXeroKeys = presentXeroMappingKeys.filter((key) => !expectedXeroSet.has(key));
  if (missingXeroKeys.length > 0 || extraXeroKeys.length > 0) {
    issues.push({
      code: 'INVARIANT_VIOLATION',
      message:
        'Xero mapping key set must exactly match LEAVE_TYPE_SUPPORTED_XERO_MAPPING_KEYS; subset or extra mappings are not allowed.',
      value: `missing=${missingXeroKeys.join(',')};extra=${extraXeroKeys.join(',')}`,
    });
  }

  for (const [key, count] of operationalSeen) {
    if (count > 1) {
      issues.push({
        code: 'DUPLICATE_OPERATIONAL_KEY',
        message: `Duplicate operational key ${key}.`,
        operationalKey: key,
        value: key,
      });
    }
  }

  for (const known of KNOWN_OPERATIONAL_KEYS) {
    if ((operationalSeen.get(known) ?? 0) === 0) {
      issues.push({
        code: 'MISSING_OPERATIONAL_KEY',
        message: `Missing operational key ${known}.`,
        operationalKey: known,
        value: known,
      });
    }
  }

  for (const [source, count] of sourceSeen) {
    if (count > 1) {
      issues.push({
        code: 'DUPLICATE_SOURCE_IDENTITY',
        message: `Duplicate source identity ${source}.`,
        value: source,
      });
    }
  }

  const personalGs = rows.filter((row) => row.operationalKey === 'PERSONAL_GENERAL_STAFF');
  const personalNurses = rows.filter((row) => row.operationalKey === 'PERSONAL_NURSES');
  const carersExtra = rows.filter((row) => row.operationalKey === 'CARERS_EXTRA_GENERAL_STAFF');
  const personalGsRow = personalGs[0];
  const personalNursesRow = personalNurses[0];
  const carersExtraRow = carersExtra[0];
  if (
    personalGs.length === 1 &&
    personalNurses.length === 1 &&
    carersExtra.length === 1 &&
    personalGsRow &&
    personalNursesRow &&
    carersExtraRow
  ) {
    const distinct =
      personalGsRow.sourceIdentity !== personalNursesRow.sourceIdentity &&
      personalGsRow.sourceIdentity !== carersExtraRow.sourceIdentity &&
      personalNursesRow.sourceIdentity !== carersExtraRow.sourceIdentity &&
      personalGsRow.xero.mappingKey !== personalNursesRow.xero.mappingKey &&
      personalGsRow.xero.mappingKey !== carersExtraRow.xero.mappingKey &&
      personalNursesRow.xero.mappingKey !== carersExtraRow.xero.mappingKey &&
      personalGsRow.cohort !== personalNursesRow.cohort;
    if (!distinct) {
      issues.push({
        code: 'INVARIANT_VIOLATION',
        message:
          'Personal general staff, personal nurses, and extra carer leave must remain exact distinct identities.',
      });
    }
  }

  if (expectedDigest !== undefined) {
    const digest = fnv1a32Hex(serializeLeaveTypeRegistryCanonical(rows));
    if (digest !== expectedDigest) {
      issues.push({
        code: 'REGISTRY_DRIFT',
        message: 'Leave type registry digest does not match expected digest.',
        value: digest,
      });
    }
  }

  return freezeValidationResult(issues);
}

const CANONICAL_LOAD_VALIDATION = validateLeaveTypeRegistry(
  Object.values(LEAVE_TYPE_REGISTRY),
  LEAVE_TYPE_REGISTRY_EXPECTED_DIGEST,
);
if (!CANONICAL_LOAD_VALIDATION.ok) {
  const first = CANONICAL_LOAD_VALIDATION.issues[0];
  throw new LeaveTypeRegistryError(
    first?.code ?? 'INVARIANT_VIOLATION',
    first?.message ?? 'Leave type registry failed canonical validation.',
    { operationalKey: first?.operationalKey, value: first?.value },
  );
}

const SOURCE_IDENTITY_INDEX: ReadonlyMap<string, LeaveTypeRegistryRow> = (() => {
  const map = new Map<string, LeaveTypeRegistryRow>();
  for (const row of Object.values(LEAVE_TYPE_REGISTRY)) {
    map.set(row.sourceIdentity, row);
  }
  return map;
})();

const IMPORT_ALIAS_INDEX: ReadonlyMap<string, LeaveTypeRegistryRow> = (() => {
  const map = new Map<string, LeaveTypeRegistryRow>();
  for (const row of Object.values(LEAVE_TYPE_REGISTRY)) {
    for (const alias of row.aliases) {
      map.set(normalizeLeaveTypeImportAlias(alias), row);
    }
  }
  return map;
})();

const DISPLAY_FAMILIES = displayFamiliesFromRows(Object.values(LEAVE_TYPE_REGISTRY));

function isSummaryDisplayFamilyToken(raw: string): boolean {
  const trimmed = String(raw).trim();
  if (trimmed.length === 0) {
    return false;
  }
  if (KNOWN_OPERATIONAL_KEY_SET.has(trimmed)) {
    return false;
  }
  const upper = trimmed.toUpperCase();
  const lower = trimmed.toLowerCase();
  const collapsed = normalizeLeaveTypeImportAlias(trimmed).replace(/[\s-]+/g, '_');
  return (
    DISPLAY_FAMILIES.has(trimmed) ||
    DISPLAY_FAMILIES.has(upper) ||
    DISPLAY_FAMILIES.has(lower) ||
    DISPLAY_FAMILIES.has(collapsed) ||
    upper === 'PERSONAL' ||
    upper === 'ANNUAL'
  );
}

export function getLeaveTypeByOperationalKey(operationalKey: string): LeaveTypeRegistryRow {
  if (KNOWN_OPERATIONAL_KEY_SET.has(operationalKey)) {
    return LEAVE_TYPE_REGISTRY[operationalKey as LeaveTypeOperationalKey];
  }
  if (isSummaryDisplayFamilyToken(operationalKey)) {
    throw new LeaveTypeRegistryError(
      'DISPLAY_FAMILY_NOT_OPERATIONAL',
      `Display family "${operationalKey}" is not an operational leave type key.`,
      { value: operationalKey },
    );
  }
  throw new LeaveTypeRegistryError(
    'UNKNOWN_OPERATIONAL_KEY',
    `Unknown operational leave type key "${operationalKey}".`,
    { value: operationalKey },
  );
}

export function getLeaveTypeBySourceIdentity(sourceIdentity: string): LeaveTypeRegistryRow {
  const row = SOURCE_IDENTITY_INDEX.get(sourceIdentity);
  if (!row) {
    throw new LeaveTypeRegistryError(
      'UNKNOWN_SOURCE_IDENTITY',
      `Unknown leave type source identity "${sourceIdentity}".`,
      { value: sourceIdentity },
    );
  }
  return row;
}

export function resolveImportAlias(alias: string): LeaveTypeRegistryRow {
  const normalized = normalizeLeaveTypeImportAlias(alias);
  if (normalized.length === 0) {
    throw new LeaveTypeRegistryError('UNKNOWN_IMPORT_ALIAS', 'Import alias is empty.', {
      value: alias,
    });
  }
  const row = IMPORT_ALIAS_INDEX.get(normalized);
  if (row) {
    return row;
  }
  if (isSummaryDisplayFamilyToken(alias) || isSummaryDisplayFamilyToken(normalized)) {
    throw new LeaveTypeRegistryError(
      'DISPLAY_FAMILY_NOT_OPERATIONAL',
      `Import alias "${alias}" names a display family and cannot select an operational leave type.`,
      { value: alias },
    );
  }
  throw new LeaveTypeRegistryError(
    'UNKNOWN_IMPORT_ALIAS',
    `Unknown leave type import alias "${alias}".`,
    { value: alias },
  );
}

deepFreeze(LEAVE_TYPE_REGISTRY);
